import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Rocket,
  Shield,
  Server,
  Database,
  Globe,
  Key,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Download,
  GitBranch,
  Eye,
  EyeOff,
  Lock,
  Cloud,
  Network,
  MonitorCheck,
  Mail,
  HardDrive,
  FileCode2,
  Play,
  Trash2,
  Clock,
  AlertTriangle,
  Copy,
  ExternalLink,
  HelpCircle,
  Terminal,
  Search,
  GitCommit,
  ShieldAlert,
  DollarSign,
  RefreshCw,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { id: "connect", label: "Azure Connection", icon: Key },
  { id: "configure", label: "Environment", icon: Server },
  { id: "deploy", label: "Deploy / Export", icon: Rocket },
];

const TIER_INFO = {
  appservice: {
    label: "App Service",
    subtitle: "Dev / Test",
    desc: "Cost-optimized. Single-instance with auto-scaling option.",
    cost: "~$13/mo (B1) to ~$73/mo (S1)",
    color: "text-blue-500",
  },
  aks: {
    label: "AKS Cluster",
    subtitle: "Staging / Production",
    desc: "Scale-ready. Multi-node Kubernetes with auto-scaling.",
    cost: "~$300+/mo (3-node D4s_v3)",
    color: "text-emerald-500",
  },
};

const SECURITY_OPTIONS = [
  { key: "key_vault", label: "Azure Key Vault", desc: "Store all secrets securely", icon: Lock, critical: true },
  { key: "private_endpoints", label: "Private Endpoints", desc: "No public access to DB/services", icon: Network, critical: true },
  { key: "managed_identity", label: "Managed Identity", desc: "Passwordless service auth", icon: Shield, critical: true },
  { key: "defender", label: "Defender for Cloud", desc: "Threat detection & security posture", icon: MonitorCheck },
  { key: "waf", label: "WAF (App Gateway)", desc: "Web application firewall", icon: Shield },
  { key: "ddos_protection", label: "DDoS Protection", desc: "Standard DDoS mitigation", icon: Cloud },
];

const SERVICES = [
  { key: "cosmos_mongodb", label: "Cosmos DB (MongoDB)", desc: "Primary data store", icon: Database, required: true },
  { key: "cosmos_gremlin", label: "Cosmos DB (Gremlin)", desc: "Resource relationship graph", icon: GitBranch },
  { key: "communication_services", label: "Communication Services", desc: "Email notifications", icon: Mail },
  { key: "app_insights", label: "Application Insights", desc: "Monitoring & alerting", icon: MonitorCheck },
  { key: "storage_account", label: "Storage Account", desc: "Backups & exports", icon: HardDrive },
];

export default function DeploymentPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);

  const fetchDeployments = useCallback(async () => {
    try {
      const res = await api().get("/deployments");
      setDeployments(res.data.deployments || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchDeployments(); }, [fetchDeployments]);

  const handleDelete = async (id, dep) => {
    const hasAzure = dep?.azure_deployment_name || ["deployed", "failed", "drift_detected", "cleaning_up"].includes(dep?.status);
    const msg = hasAzure
      ? `This will DELETE the deployment record AND clean up Azure resources in "${dep?.azure_config?.resource_group_name || 'rg-niyantproduction-control'}". Continue?`
      : "Delete this deployment?";
    if (!window.confirm(msg)) return;
    try {
      const res = await api().delete(`/deployments/${id}`);
      setDeployments(prev => prev.filter(d => d.id !== id));
      if (res.data.azure_cleanup) {
        toast.success(res.data.azure_cleanup);
      } else {
        toast.success("Deployment deleted");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="animate-page-enter space-y-6" data-testid="deployment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Cloud Infrastructure</h1>
          <p className="text-sm text-[var(--n-text-muted)] mt-1">
            Provision and monitor Niyant infrastructure in Azure. Zero-password architecture.
          </p>
        </div>
        <Button
          onClick={() => navigate("/deployments/new")}
          className="bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90"
          data-testid="new-deployment-btn"
        >
          <Rocket className="w-4 h-4 mr-2" /> New Deployment
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" /></div>
      ) : deployments.length === 0 ? (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] border-dashed">
          <CardContent className="py-16 text-center">
            <Cloud className="w-10 h-10 mx-auto text-[var(--n-text-muted)] mb-3" />
            <h3 className="text-sm font-medium mb-1">No deployments yet</h3>
            <p className="text-xs text-[var(--n-text-muted)] max-w-sm mx-auto">
              Create your first deployment to provision Niyant's infrastructure into your Azure tenant with enterprise-grade security.
            </p>
            <Button onClick={() => navigate("/deployments/new")} className="mt-4 bg-[var(--hig-accent)] text-white hover:bg-[var(--hig-accent)]/90" data-testid="empty-state-new-btn">
              <Rocket className="w-4 h-4 mr-2" /> Create Deployment
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deployments.map((dep) => (
            <DeploymentCard
              key={dep.id}
              deployment={dep}
              api={api}
              onDelete={() => handleDelete(dep.id, dep)}
              onRefresh={fetchDeployments}
              onPreview={(dep) => setTemplatePreview(dep)}
            />
          ))}
        </div>
      )}

      {templatePreview && (
        <TemplatePreviewDialog
          deployment={templatePreview}
          api={api}
          onClose={() => setTemplatePreview(null)}
        />
      )}
    </div>
  );
}

function DeploymentCard({ deployment: dep, api, onDelete, onRefresh, onPreview }) {
  const [acting, setActing] = useState(null);
  const [driftResults, setDriftResults] = useState(null);
  const [showDrift, setShowDrift] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [health, setHealth] = useState(null);
  const [showModify, setShowModify] = useState(false);
  const [modifyTasks, setModifyTasks] = useState([]);

  // Auto-poll status when deploying
  useEffect(() => {
    if (dep.status !== "deploying") return;
    const interval = setInterval(async () => {
      try {
        const res = await api().get(`/deployments/${dep.id}/status`);
        if (res.data.status !== "deploying") onRefresh();
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [dep.status, dep.id, api, onRefresh]);

  // Health check for deployed environments
  useEffect(() => {
    if (!["deployed", "drift_detected"].includes(dep.status) || !dep.endpoint_url) return;
    api().get(`/deployments/${dep.id}/health`).then(r => setHealth(r.data)).catch(() => {});
  }, [dep.status, dep.endpoint_url, dep.id, api]);

  const statusColors = {
    draft: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    validated: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    generated: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    deploying: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    deployed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    drift_detected: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    cleaning_up: "bg-orange-500/10 text-orange-600 dark:text-orange-400 animate-pulse",
    failed: "bg-red-500/10 text-red-600 dark:text-red-400",
  };

  const handleAction = async (action) => {
    setActing(action);
    try {
      if (action === "validate") {
        const res = await api().post(`/deployments/${dep.id}/validate`);
        toast[res.data.status === "success" ? "success" : "error"](res.data.message);
      } else if (action === "generate") {
        const res = await api().post(`/deployments/${dep.id}/generate`);
        toast.success(`Generated ${res.data.bicep_files.length} Bicep files`);
      } else if (action === "execute") {
        const res = await api().post(`/deployments/${dep.id}/execute`);
        toast[res.data.status === "error" ? "error" : "success"](res.data.message);
      } else if (action === "push-git") {
        const res = await api().post(`/deployments/${dep.id}/push-to-git`);
        if (res.data.status === "error") {
          toast.error(res.data.errors?.[0]?.error || "Git push failed");
        } else {
          toast.success(`Pushed ${res.data.pushed} files to ${res.data.repo}`);
          if (res.data.url) window.open(res.data.url, "_blank");
        }
      } else if (action === "drift") {
        const res = await api().post(`/deployments/${dep.id}/check-drift`);
        if (res.data.status === "error") {
          toast.error(res.data.message || "Drift check failed");
        } else if (res.data.total > 0) {
          toast.warning(`${res.data.total} drift(s) detected`);
          setDriftResults(res.data.drifts);
          setShowDrift(true);
        } else {
          toast.success("No drift detected");
          setDriftResults([]);
          setShowDrift(true);
        }
      } else if (action === "cleanup") {
        if (!window.confirm(`This will DELETE the entire resource group "${dep.azure_config?.resource_group_name}" and ALL resources inside it. This cannot be undone. Continue?`)) return;
        const res = await api().post(`/deployments/${dep.id}/cleanup`);
        if (res.data.status === "error") {
          toast.error(res.data.message);
        } else {
          toast.success(res.data.message);
        }
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${action} failed`);
    } finally {
      setActing(null);
    }
  };

  const tierInfo = TIER_INFO[dep.compute_tier] || TIER_INFO.appservice;
  const secCount = Object.values(dep.security_options || {}).filter(Boolean).length;

  return (
    <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]" data-testid={`deployment-card-${dep.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2.5 mb-1">
              <h3 className="text-sm font-medium">{dep.name}</h3>
              <Badge className={`text-[9px] px-1.5 py-0 ${statusColors[dep.status] || statusColors.draft}`}>{dep.status}</Badge>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0">{dep.environment_type}</Badge>
            </div>
            <div className="flex items-center gap-4 text-[10px] text-[var(--n-text-muted)]">
              <span className={tierInfo.color}>{tierInfo.label}</span>
              <span>{dep.azure_config?.region}</span>
              <span>{secCount}/6 security</span>
              <span>{dep.credential_storage === "keyvault" ? "Secrets in Key Vault" : "Secrets not stored"}</span>
              {dep.environment_id && <span className="text-[var(--hig-accent)]">Env linked</span>}
            </div>
            {/* Endpoint URL + Health */}
            {dep.endpoint_url && (
              <div className="flex items-center gap-2 mt-1.5" data-testid={`endpoint-${dep.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${health?.healthy === true ? "bg-emerald-500" : health?.healthy === false ? "bg-red-500" : "bg-[var(--n-text-muted)]"}`} title={health?.healthy === true ? `Healthy (${health.status_code})` : health?.healthy === false ? `Unhealthy: ${health.error || health.status_code}` : "Checking..."} />
                <a href={dep.endpoint_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[var(--hig-accent)] hover:underline truncate max-w-xs font-mono" data-testid={`endpoint-url-${dep.id}`}>
                  {dep.endpoint_url}
                </a>
                <button onClick={() => { navigator.clipboard.writeText(dep.endpoint_url); toast.success("URL copied"); }} className="text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)]" title="Copy URL">
                  <Copy className="w-3 h-3" />
                </button>
                <button onClick={() => { setHealth(null); api().get(`/deployments/${dep.id}/health`).then(r => setHealth(r.data)).catch(() => setHealth({ healthy: false })); }} className="text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)]" title="Recheck health">
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {dep.status === "draft" && (
              <Button size="sm" variant="outline" onClick={() => handleAction("validate")} disabled={!!acting} className="text-[10px] h-7 px-2" data-testid={`validate-btn-${dep.id}`}>
                {acting === "validate" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Validate
              </Button>
            )}
            {["validated", "generated"].includes(dep.status) && (
              <Button size="sm" variant="outline" onClick={() => handleAction("generate")} disabled={!!acting} className="text-[10px] h-7 px-2" data-testid={`generate-btn-${dep.id}`}>
                {acting === "generate" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileCode2 className="w-3 h-3 mr-1" />} Generate IaC
              </Button>
            )}
            {dep.status === "generated" && (
              <>
                <Button size="sm" onClick={() => onPreview(dep)} className="text-[10px] h-7 px-2 bg-indigo-600 text-white hover:bg-indigo-700" data-testid={`preview-btn-${dep.id}`}>
                  <Eye className="w-3 h-3 mr-1" /> Preview
                </Button>
                {dep.git_config?.provider && dep.git_config?.pat && (
                  <Button size="sm" onClick={() => handleAction("push-git")} disabled={!!acting} className="text-[10px] h-7 px-2" variant="outline" data-testid={`git-push-btn-${dep.id}`}>
                    {acting === "push-git" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <GitCommit className="w-3 h-3 mr-1" />} Push to Git
                  </Button>
                )}
                <Button size="sm" onClick={() => handleAction("execute")} disabled={!!acting} className="text-[10px] h-7 px-2 bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`execute-btn-${dep.id}`}>
                  {acting === "execute" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />} Deploy
                </Button>
              </>
            )}
            {dep.status === "deploying" && (
              <Badge className="bg-amber-500/10 text-amber-600 animate-pulse text-[9px]">
                <Loader2 className="w-3 h-3 animate-spin mr-1" /> Deploying...
              </Badge>
            )}
            {dep.status === "failed" && (
              <>
                <Button size="sm" variant="outline" onClick={async () => {
                  setActing("check-status");
                  try {
                    const res = await api().get(`/deployments/${dep.id}/status`);
                    const msg = res.data.control_plane?.state || res.data.data_plane?.state || res.data.status;
                    if (msg.includes("Failed:")) {
                      toast.error(msg, { duration: 10000 });
                    } else {
                      toast.info(`Status: ${msg}`);
                    }
                    onRefresh();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Status check failed");
                  } finally {
                    setActing(null);
                  }
                }} disabled={!!acting} className="text-[10px] h-7 px-2" data-testid={`check-status-btn-${dep.id}`}>
                  {acting === "check-status" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Check Status
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleAction("generate")} disabled={!!acting} className="text-[10px] h-7 px-2" data-testid={`regenerate-btn-${dep.id}`}>
                  {acting === "generate" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileCode2 className="w-3 h-3 mr-1" />} Regenerate IaC
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleAction("execute")} disabled={!!acting} className="text-[10px] h-7 px-2 bg-amber-600 text-white hover:bg-amber-700" data-testid={`retry-btn-${dep.id}`}>
                  {acting === "execute" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />} Retry Deploy
                </Button>
                <Badge className="bg-red-500/10 text-red-600 text-[9px]">
                  <XCircle className="w-3 h-3 mr-1" /> Failed
                </Badge>
              </>
            )}
            {["deployed", "drift_detected"].includes(dep.status) && (
              <>
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await api().get(`/deployments/${dep.id}/modify-tasks`);
                    setModifyTasks(res.data.tasks || []);
                    setShowModify(true);
                  } catch { toast.error("Failed to load tasks"); }
                }} className="text-[10px] h-7 px-2" data-testid={`modify-btn-${dep.id}`}>
                  <SlidersHorizontal className="w-3 h-3 mr-1" /> Modify
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleAction("drift")} disabled={!!acting} className="text-[10px] h-7 px-2" data-testid={`drift-check-btn-${dep.id}`}>
                  {acting === "drift" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldAlert className="w-3 h-3 mr-1" />} Check Drift
                </Button>
                {dep.status === "drift_detected" ? (
                  <Badge className="bg-orange-500/10 text-orange-600 text-[9px]">
                    <AlertTriangle className="w-3 h-3 mr-1" /> Drift
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-500/10 text-emerald-600 text-[9px]">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Live
                  </Badge>
                )}
              </>
            )}
            {["failed", "deployed", "drift_detected", "cleaning_up"].includes(dep.status) && (
              <Button size="sm" variant="outline" onClick={() => handleAction("cleanup")} disabled={!!acting || dep.status === "cleaning_up"} className="text-[10px] h-7 px-2 text-orange-600 border-orange-300 hover:bg-orange-500/10" data-testid={`cleanup-btn-${dep.id}`}>
                {acting === "cleanup" || dep.status === "cleaning_up" ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />} {dep.status === "cleaning_up" ? "Cleaning..." : "Cleanup Azure"}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onDelete} className="text-[10px] h-7 w-7 p-0 text-red-500 hover:bg-red-500/10" data-testid={`delete-btn-${dep.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {dep.deployment_log?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[var(--n-border-2)]">
            {!showLogs ? (
              <button
                onClick={(e) => { e.stopPropagation(); setShowLogs(true); }}
                className="text-xs text-[var(--n-text-muted)] hover:text-[var(--hig-accent)] flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-[var(--n-bg)] transition-colors"
                data-testid={`show-all-logs-${dep.id}`}
              >
                <ChevronRight className="w-4 h-4" />
                Show {dep.deployment_log.length} log entries
              </button>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowLogs(false); }}
                    className="text-xs text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)] flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-[var(--n-bg)] transition-colors"
                    data-testid={`collapse-logs-${dep.id}`}
                  >
                    <ChevronRight className="w-4 h-4 rotate-90" /> Collapse
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const logText = [...dep.deployment_log].reverse().map(l =>
                        `[${l.timestamp}] [${l.status}] ${l.action}: ${l.message}`
                      ).join("\n");
                      navigator.clipboard.writeText(logText);
                      toast.success("Logs copied to clipboard");
                    }}
                    className="text-xs text-[var(--hig-accent)] hover:underline flex items-center gap-1.5 py-1.5 px-2"
                    data-testid={`copy-logs-${dep.id}`}
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy All
                  </button>
                </div>
                <div className="space-y-2">
                  {[...dep.deployment_log].reverse().map((log, i) => (
                    <div key={i} className={`flex items-start gap-3 text-xs p-2 rounded-lg ${log.status === "error" ? "bg-red-500/5" : ""}`}>
                      <Clock className="w-3.5 h-3.5 text-[var(--n-text-muted)] mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[var(--n-text-muted)] text-[11px]">{new Date(log.timestamp).toLocaleString()}</span>
                          <Badge className={`text-[9px] px-1.5 py-0 ${log.status === "error" ? "bg-red-500/10 text-red-500" : log.status === "success" ? "bg-emerald-500/10 text-emerald-500" : "bg-blue-500/10 text-blue-500"}`}>{log.status}</Badge>
                          <span className="text-[10px] text-[var(--n-text-muted)]">{log.action}</span>
                        </div>
                        <p className={`mt-1 break-all leading-relaxed ${log.status === "error" ? "text-red-600 dark:text-red-400 font-mono text-[11px]" : "text-[var(--n-text-secondary)]"}`}>{log.message}</p>
                      </div>
                      {log.status === "error" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`[${log.timestamp}] ${log.action}: ${log.message}`); toast.success("Error copied"); }}
                          className="shrink-0 p-1.5 rounded-lg hover:bg-red-500/10 text-red-400"
                          title="Copy error"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {/* Drift results */}
        {showDrift && driftResults && (
          <div className="mt-3 pt-3 border-t border-[var(--n-border-2)]" data-testid="drift-results">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[9px] text-[var(--n-text-muted)] font-medium">
                {driftResults.length === 0 ? "No Drift Detected" : `${driftResults.length} Drift(s) Found`}
              </p>
              <button onClick={() => setShowDrift(false)} className="text-[9px] text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)]">Hide</button>
            </div>
            {driftResults.length === 0 ? (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/5 text-[10px] text-emerald-600">
                <CheckCircle2 className="w-3.5 h-3.5" /> All resources match the deployment template.
              </div>
            ) : (
              <div className="space-y-1.5">
                {driftResults.map((d, i) => (
                  <div key={i} className={`p-2 rounded-lg text-[10px] flex items-start gap-2 ${
                    d.severity === "critical" ? "bg-red-500/5 text-red-600" :
                    d.severity === "warning" ? "bg-amber-500/5 text-amber-600" :
                    "bg-blue-500/5 text-blue-600"
                  }`} data-testid={`drift-item-${i}`}>
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <div>
                      <span className="font-medium">{d.resource}</span>
                      <span className="text-[var(--n-text-muted)] ml-1">({d.type})</span>
                      <p className="text-[9px] mt-0.5">{d.drift}</p>
                      <p className="text-[9px] text-[var(--n-text-muted)]">Expected: {d.expected} | Actual: {d.actual}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {dep.last_drift_check && (
              <p className="text-[8px] text-[var(--n-text-muted)] mt-2">Last checked: {new Date(dep.last_drift_check).toLocaleString()}</p>
            )}
          </div>
        )}
        {/* Modify Tasks Panel */}
        {showModify && (
          <div className="mt-3 pt-3 border-t border-[var(--n-border-2)]" data-testid="modify-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-medium text-[var(--n-text-muted)]">Available Modifications</span>
              <button onClick={() => setShowModify(false)} className="text-[9px] text-[var(--n-text-muted)] hover:text-[var(--n-text-secondary)]">Close</button>
            </div>
            <div className="space-y-2">
              {modifyTasks.map(task => (
                <div key={task.id} className={`flex items-center justify-between p-2.5 rounded-lg border ${task.completed ? "border-emerald-500/20 bg-emerald-500/5" : "border-[var(--n-border-2)]"}`} data-testid={`modify-task-${task.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium">{task.title}</span>
                      {task.completed && <Badge className="text-[8px] px-1 py-0 bg-emerald-500/10 text-emerald-500">Done</Badge>}
                    </div>
                    <p className="text-[9px] text-[var(--n-text-muted)] mt-0.5">{task.description}</p>
                  </div>
                  {!task.completed && task.available && (
                    <Button size="sm" variant="outline" disabled={!!acting} className="text-[10px] h-7 px-2 ml-3 shrink-0"
                      onClick={async () => {
                        setActing(`modify_${task.id}`);
                        try {
                          const res = await api().post(`/deployments/${dep.id}/modify`, { task: task.id });
                          if (res.data.status === "success") {
                            toast.success(res.data.message);
                            setModifyTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: true } : t));
                          } else if (res.data.status === "info") {
                            toast.info(res.data.message);
                          } else {
                            toast.error(res.data.message);
                          }
                          onRefresh();
                        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                        finally { setActing(null); }
                      }}
                      data-testid={`run-modify-${task.id}`}
                    >
                      {acting === `modify_${task.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Run"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplatePreviewDialog({ deployment, api, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    api().get(`/deployments/${deployment.id}/templates`)
      .then(res => {
        setTemplates(res.data);
        const files = Object.keys(res.data.bicep || {});
        if (files.length > 0) setSelectedFile(files[0]);
      })
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  }, [api, deployment.id]);

  const copyContent = () => {
    if (selectedFile && templates?.bicep?.[selectedFile]) {
      navigator.clipboard.writeText(templates.bicep[selectedFile]);
      toast.success("Copied to clipboard");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[var(--n-card)] border-[var(--n-border-2)] max-w-4xl max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-[var(--n-border-2)]">
          <DialogTitle className="text-sm font-heading flex items-center gap-2">
            <FileCode2 className="w-4 h-4 text-[var(--hig-accent)]" /> Template Preview — {deployment.name}
          </DialogTitle>
          <DialogDescription className="text-[10px]">
            Generated Bicep templates for your deployment. Copy individual files or download all.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="flex h-[60vh]">
            {/* File tree */}
            <div className="w-52 border-r border-[var(--n-border-2)] overflow-y-auto p-2">
              {Object.keys(templates?.bicep || {}).map(file => (
                <button
                  key={file}
                  onClick={() => setSelectedFile(file)}
                  className={`w-full text-left text-[10px] px-2.5 py-1.5 rounded transition-colors ${selectedFile === file ? "bg-[var(--hig-accent)]/10 text-[var(--hig-accent)] font-medium" : "text-[var(--n-text-secondary)] hover:bg-[var(--n-bg)]"}`}
                  data-testid={`file-${file.replace(/[/.]/g, "-")}`}
                >
                  <FileCode2 className="w-3 h-3 inline mr-1.5" />
                  {file}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--n-border-2)]">
                <span className="text-[10px] font-mono text-[var(--n-text-muted)]">{selectedFile}</span>
                <Button variant="ghost" size="sm" onClick={copyContent} className="text-[10px] h-6 px-2" data-testid="copy-template-btn">
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <pre className="flex-1 overflow-auto p-4 text-[10px] font-mono leading-relaxed text-[var(--n-text-secondary)] bg-[var(--n-bg)]">
                {selectedFile && templates?.bicep?.[selectedFile]}
              </pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
